import { ImoovaImage } from "@/app/Media/Components/Image";
import { Suspense, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { useOfficeActions } from "@/app/Offices/Hooks/useOfficeActions";
import { Badge } from "@/components/catalyst/badge";
import { ActionGroupButtons } from "@/lib/Components/Actions/ActionGroupButtons";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { updateSupplierOfficeMutation } from "@/app/Offices/GraphQL/supplierOfficeMutations";
import {
  ContactType,
  UpdateSupplierOfficeInput,
  ModelType,
} from "@/gql/graphql";
import { useOfficeDrawer } from "@/app/Offices/Components/Drawer/useOfficeDrawer";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { SideTabs } from "@/app/Offices/Components/Drawer/OfficeDrawerTabs";
import { ActivityLogFeed } from "@/app/ActivityLogs/Components/ActivityLogFeed";
import { SupplierOfficeForm } from "@/app/Offices/Forms/SupplierOfficeForm";
import { OfficeNotificationForm } from "@/app/Offices/Forms/OfficeNotificationForm";
import { SupplierOfficeOpenHoursForm } from "@/app/Offices/Screens/OfficeOpenHoursScreen";
import { officeValidation } from "@/app/Offices/Validators/officeValidation";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { ContactInput } from "@/app/Contacts/Components/ContactInput";
import {
  fromOpenDaysSchema,
  toOpenDaysSchema,
} from "@/app/Suppliers/Utils/mapClosedDays";
import { ClosedDatesForm } from "@/app/Suppliers/Components/ClosedDatesForm";
import { ClosedPickupDatesForm } from "@/app/Offices/Components/ClosedPickupDatesForm";
import { ClosedDropoffDatesForm } from "@/app/Offices/Components/ClosedDropoffDatesForm";
import {
  IllustrationBinoculars,
  IllustrationCalendar,
} from "@/assets/Illustrations";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";

export function OfficeDrawer() {
  const navigate = useNavigate();
  const search = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}",
  });
  const [activeTab, setActiveTab] = useState("details");

  const isOpen = !!search.officeId;

  const handleClose = () => {
    navigate({
      to: ".",
      search: (prev: any) => {
        return {
          ...prev,
          officeId: undefined,
        };
      },
    });
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative">
      <DialogBackdrop
        transition
        className="data-closed:opacity-0 fixed inset-0 bg-gray-900/50 transition-opacity duration-500 ease-in-out"
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-6 right-6 flex max-w-full pl-10 sm:pl-16">
            <DialogPanel
              transition
              className="data-closed:translate-x-full pointer-events-auto relative w-screen max-w-4xl transform rounded-xl transition duration-500 ease-in-out sm:duration-700"
            >
              <TransitionChild>
                <div className="data-closed:opacity-0 absolute left-0 top-0 -ml-8 flex pr-2 pt-4 duration-500 ease-in-out sm:-ml-10 sm:pr-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="relative rounded-md text-gray-400 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                  >
                    <span className="absolute -inset-2.5" />
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon aria-hidden="true" className="size-6" />
                  </button>
                </div>
              </TransitionChild>

              {search.officeId ? (
                <div className="relative h-full overflow-hidden rounded-2xl bg-white shadow-xl">
                  {/* Main Content */}
                  <div className="flex h-full flex-col overflow-hidden">
                    <Suspense
                      fallback={
                        <div className="flex h-full items-center justify-center">
                          <Spinner />
                        </div>
                      }
                    >
                      {/* Header */}
                      <DialogHeading
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                      />

                      {/* Content */}
                      <div className="flex-1 overflow-y-auto px-6 py-6">
                        <TabContent activeTab={activeTab} />
                      </div>
                    </Suspense>
                  </div>
                </div>
              ) : null}
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function TabContent({ activeTab }: { activeTab: string }) {
  switch (activeTab) {
    case "details":
      return <OfficeDrawerDetailsScreen />;
    case "hours":
      return <OfficeDrawerHoursScreen />;
    case "closed-days":
      return <OfficeDrawerClosedDaysScreen />;
    case "images":
      return <OfficeDrawerImagesScreen />;
    case "billing":
      return <OfficeDrawerBillingScreen />;
    case "activity":
      return <OfficeDrawerActivityScreen />;
    default:
      return <OfficeDrawerDetailsScreen />;
  }
}

function DialogHeading({
  activeTab,
  setActiveTab,
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) {
  const office = useOfficeDrawer();
  const locationMedia = office.address?.locationMedia;

  return (
    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-4">
          {locationMedia ? (
            <ImoovaImage
              media={locationMedia}
              alt={office.name}
              className="h-14 w-14 rounded-lg object-cover ring-1 ring-gray-200"
            />
          ) : null}
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {office.name}
            </h2>
            <p className="text-sm text-gray-500">
              {office.address.formatted_address_line_1}{" "}
              {office.address.formatted_address_line_2}
            </p>
            {office.archived_at ? <Badge color="purple">archived</Badge> : null}
          </div>
        </div>

        {/* Action buttons */}
        <OfficeDrawerActions office={office} />
      </div>

      {/* Tabs */}
      <div className="px-6">
        <SideTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </div>
  );
}

function OfficeDrawerDetailsScreen() {
  const office = useOfficeDrawer();
  const { mutateAsync } = useGqlMutation(updateSupplierOfficeMutation);

  async function handleSubmit(values: UpdateSupplierOfficeInput) {
    await mutateAsync({
      id: office.id,
      input: values,
    });
  }

  return (
    <div className="space-y-8">
      {/* Basic Information */}
      <RecordScreenFormCard<UpdateSupplierOfficeInput>
        title="Details"
        validationSchema={toFormikValidationSchema(officeValidation)}
        initialValues={{
          name: office.name,
          code: office.code,
          city: {
            connect: office.city.id,
          },
          supplier: {
            connect: office.supplier.id,
          },
          address: {
            upsert: {
              id: office.address?.id,
              address_line_1: office.address?.address_line_1,
              address_line_2: office.address?.address_line_2,
              city: office.address?.city,
              state: office.address?.state,
              country: office.address?.country,
              postcode: office.address?.postcode,
            },
          },
          email: office.email,
          phone: office.phone,
          pickup_instructions: office.pickup_instructions,
        }}
        onSubmit={handleSubmit}
      >
        <SupplierOfficeForm
          initialLat={office.address?.lat}
          initialLng={office.address?.lng}
        />
      </RecordScreenFormCard>

      {/* Notifications */}
      <RecordScreenFormCard<UpdateSupplierOfficeInput>
        title="Notifications"
        initialValues={{
          booking_email: office.booking_email,
          cc_booking_to_departure_office: office.cc_booking_to_departure_office,
        }}
        onSubmit={handleSubmit}
      >
        <OfficeNotificationForm />
      </RecordScreenFormCard>
    </div>
  );
}

function OfficeDrawerHoursScreen() {
  const office = useOfficeDrawer();
  const { mutateAsync } = useGqlMutation(updateSupplierOfficeMutation);

  return (
    <div className="space-y-8">
      {/* Departure Hours */}
      <RecordScreenFormCard
        enableReinitialize={true}
        onSubmit={async (values) => {
          await mutateAsync({
            id: office.id,
            input: {
              departure_hours: values.custom_hours
                ? values.departure_hours
                : null,
            },
          });
        }}
        initialValues={{
          custom_hours: office.departure_hours !== null,
          departure_hours:
            office.departure_hours ?? office.supplier.departure_hours,
        }}
        title="Departure hours"
      >
        <SupplierOfficeOpenHoursForm name="departure_hours" />
      </RecordScreenFormCard>

      {/* Delivery Hours */}
      <RecordScreenFormCard
        enableReinitialize={true}
        onSubmit={async (values) => {
          await mutateAsync({
            id: office.id,
            input: {
              delivery_hours: values.custom_hours
                ? values.delivery_hours
                : null,
            },
          });
        }}
        initialValues={{
          custom_hours: office.delivery_hours !== null,
          delivery_hours:
            office.delivery_hours ?? office.supplier.delivery_hours,
        }}
        title="Delivery hours"
      >
        <SupplierOfficeOpenHoursForm name="delivery_hours" />
      </RecordScreenFormCard>
    </div>
  );
}

function OfficeDrawerClosedDaysScreen() {
  const office = useOfficeDrawer();
  const { mutateAsync } = useGqlMutation(updateSupplierOfficeMutation);

  return (
    <div className="space-y-8">
      <RecordScreenFormCard
        onSubmit={async (values) => {
          const closedDates = toOpenDaysSchema(values.closed_dates);
          await mutateAsync({
            id: office.id,
            input: {
              closed_dates: closedDates,
            },
          });
        }}
        title="Closed Dates"
        initialValues={{
          closed_dates: fromOpenDaysSchema(office.closed_dates),
        }}
      >
        <ClosedDatesForm
          emptyState={
            <EmptyState
              Icon={IllustrationCalendar}
              header="No closed dates"
              subheader="Add date(s) you want to close this office on"
            />
          }
        />
      </RecordScreenFormCard>

      <RecordScreenFormCard
        initialValues={{
          closed_departure_dates: office.closed_departure_dates ?? [],
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: office.id,
            input: {
              closed_departure_dates: values.closed_departure_dates,
            },
          });
        }}
        title="Closed Dates (Pickup Only)"
      >
        <ClosedPickupDatesForm />
      </RecordScreenFormCard>

      <RecordScreenFormCard
        initialValues={{
          closed_delivery_dates: office.closed_delivery_dates ?? [],
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: office.id,
            input: {
              closed_delivery_dates: values.closed_delivery_dates,
            },
          });
        }}
        title="Closed Dates (Drop-off Only)"
      >
        <ClosedDropoffDatesForm />
      </RecordScreenFormCard>
    </div>
  );
}

function OfficeDrawerImagesScreen() {
  const office = useOfficeDrawer();
  const locationMedia = office.address?.locationMedia;

  if (!locationMedia) {
    return (
      <EmptyState
        Icon={IllustrationBinoculars}
        header="No images"
        subheader="The location image is generated once the office address has coordinates"
      />
    );
  }

  return (
    <RecordScreenCard
      title="Location"
      subtitle="Generated from the office address coordinates"
    >
      <ImoovaImage
        media={locationMedia}
        alt={`Map of ${office.name}`}
        className="w-full max-w-xl rounded-xl ring-1 ring-gray-200"
      />
    </RecordScreenCard>
  );
}

function OfficeDrawerBillingScreen() {
  const office = useOfficeDrawer();
  const { mutateAsync } = useGqlMutation(updateSupplierOfficeMutation);

  async function handleSubmit(values: UpdateSupplierOfficeInput) {
    await mutateAsync({
      id: office.id,
      input: values,
    });
  }

  return (
    <div className="space-y-8">
      {/* Billing */}
      <RecordScreenFormCard<UpdateSupplierOfficeInput>
        title="Billing"
        initialValues={{
          billing_entity_id: office.billing_entity_id,
        }}
        onSubmit={handleSubmit}
      >
        <ContactInput
          name="billing_entity_id"
          label="Custom billing entity"
          tooltip="Add a different billing entity for relocations delivering to this office"
          getQueryVariables={(search) => {
            return {
              page: 1,
              first: 20,
              search,
              managedById: office.supplier.managedBy?.id,
              type: [ContactType.Entity, ContactType.Organisation],
            };
          }}
          initialFixtureValues={
            office.supplier.managedBy?.contact
              ? {
                  type: ContactType.Entity,
                  parentContact: {
                    connect: office.supplier.managedBy.contact.id,
                  },
                }
              : undefined
          }
        />
      </RecordScreenFormCard>
    </div>
  );
}

function OfficeDrawerActivityScreen() {
  const office = useOfficeDrawer();
  return (
    <div className="">
      <ActivityLogFeed
        modelId={office.id}
        modelType={ModelType.SupplierOffice}
      />
    </div>
  );
}

function OfficeDrawerActions({ office }: { office: any }) {
  const getActions = useOfficeActions();

  if (!office) {
    return null;
  }

  const actionGroups = getActions(office);

  return <ActionGroupButtons buttons={actionGroups} />;
}
